import {Component, forwardRef, inject, Injector, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor, FormBuilder,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from "@angular/forms";
import {distinctUntilChanged, Subject, takeUntil} from "rxjs";
import {isArray, parseDate} from "ngx-bootstrap/chronos";
import {dateToIsoDateStringWithCorrectTimezone} from "../../../utils/date-to-iso-date-string-with-correct-timezone";
import {addDays} from "date-fns";
import {DateTimeSelectorComponent, TIME_MODE} from '../../date-time-selector/date-time-selector.component';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-static-date-range-filter',
  templateUrl: './static-date-range-filter.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StaticDateRangeFilterComponent),
      multi: true,
    }
  ],
  imports: [ReactiveFormsModule, DateTimeSelectorComponent, TranslatePipe],
  standalone: true
})
export class StaticDateRangeFilterComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private readonly injector = inject(Injector);

  private readonly fb = inject(FormBuilder);

  public formGroup = this.fb.group({
    min: this.fb.control<string | null>(null),
    max: this.fb.control<string | null>(null),
  });

  @Input() public placeholder = ''
  @Input() public label!: string


  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl<string[] | string | null>

  @Input()
  public formControlName!: string;

  @Input()
  public name?: string;

  @Input()
  public inline = true;

  @Input()
  public autofillEndDate: boolean = false;

  @Input()
  public autofillDayRange: number = 1;
  protected readonly TIME_MODE = TIME_MODE;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor() {
    this.formGroup.valueChanges.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))).subscribe(
      v => {
        if ((v.min && v.max) || (v.min && !v.max)) {
          this.control.setValue((v.min ?? '') + ',' + (v.max ?? ''), {emitEvent: true});
        } else {
          this.control.setValue(null, {emitEvent: true});
        }
      }
    )
  }

  public get control(): FormControl<string[] | string | null> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <FormControl<string[] | string | null>>this.formControl ?? this.controlContainer.control.get(this.formControlName);
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(value: string[] | string | null): void {
    // First and second case is for relative_date/dateSelector => from_until
    // Third case is for a selected relative_date/dateSelector that is not from_until
    // Fourth case is when relative_date/dateSelector is null
    if (value && isArray(value) && value.length === 2) {
      this.formGroup.patchValue({
        min: value[0],
        max: value[1]
      }, {emitEvent: false})
    } else if (value && typeof value === 'string' && value.includes(',')) {
      const split = value.split(',')
      this.formGroup.patchValue({
        min: split[0] ?? null,
        max: split[1] ?? null
      }, {emitEvent: false})
    } else {
      this.formGroup.controls.min.reset('', {emitEvent: false});
      this.formGroup.controls.max.reset('', {emitEvent: false});
    }
    if (this.control.getRawValue() !== value) {
      this.control.reset(value)
    }
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  public ngOnInit() {
    if (this.autofillEndDate) {
      this.formGroup.controls.min.valueChanges.pipe(
        distinctUntilChanged(),
        takeUntil(this.onDestroy$)
      ).subscribe({
        next: value => {
          if (value) {
            this.formGroup.controls.max.setValue(
              dateToIsoDateStringWithCorrectTimezone(
                addDays(parseDate(new Date(value)), this.autofillDayRange)
              )
            )
          }
        }
      })
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }
}
